import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import PhoneInput, { phoneDigitsValid } from "../components/PhoneInput";
import { toast } from "sonner";
import { User, Building2, Lock, Upload } from "lucide-react";

export default function Profile() {
  const { user: authUser, activeClinic } = useAuth();
  const [tab, setTab] = useState("account");

  // Account section state
  const [account, setAccount] = useState({ name: "", phone: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Clinic section state
  const [clinic, setClinic] = useState({ name: "", address: "", phone: "", rfc: "", website: "" });
  const [savingClinic, setSavingClinic] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (authUser) {
      setAccount({ name: authUser.name || "", phone: authUser.phone || "" });
    }
    if (activeClinic) {
      setClinic({
        name: activeClinic.name || "",
        address: activeClinic.address || "",
        phone: activeClinic.phone || "",
        rfc: activeClinic.rfc || "",
        website: activeClinic.website || "",
      });
      if (activeClinic.logo_url) setLogoPreview(activeClinic.logo_url);
    }
  }, [authUser, activeClinic]);

  const saveAccount = async () => {
    if (!account.name.trim()) { toast.error("El nombre no puede estar vacío"); return; }
    if (!phoneDigitsValid(account.phone, false)) { toast.error("El teléfono debe tener 10 dígitos, o déjalo vacío"); return; }
    setSavingAccount(true);
    try {
      await api.patch("/users/me", { name: account.name, phone: account.phone });
      toast.success("Perfil actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSavingAccount(false);
    }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error("Completa todos los campos"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Las contraseñas nuevas no coinciden"); return; }
    if (pwForm.new_password.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    setSavingPw(true);
    try {
      await api.post("/users/me/password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Contraseña actualizada");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contraseña actual incorrecta");
    } finally {
      setSavingPw(false);
    }
  };

  const saveClinic = async () => {
    if (!activeClinic?.id) return;
    if (!clinic.name.trim()) { toast.error("El nombre de la clínica no puede estar vacío"); return; }
    if (!phoneDigitsValid(clinic.phone, false)) { toast.error("El teléfono debe tener 10 dígitos, o déjalo vacío"); return; }
    setSavingClinic(true);
    try {
      await api.patch(`/clinics/${activeClinic.id}`, clinic);
      toast.success("Clínica actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSavingClinic(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile || !activeClinic?.id) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      const { data } = await api.post(`/clinics/${activeClinic.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoPreview(data.logo_url);
      setLogoFile(null);
      toast.success("Logo actualizado");
    } catch {
      toast.error("No se pudo subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-3xl mx-auto w-full">
      <PageHeader title="Mi perfil" subtitle="Configura tu cuenta y los datos de tu clínica." />

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setTab("account")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "account" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <User className="w-4 h-4" /> Mi cuenta
        </button>
        <button
          onClick={() => setTab("clinic")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "clinic" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <Building2 className="w-4 h-4" /> Mi clínica
        </button>
      </div>

      {tab === "account" && (
        <div className="space-y-4">
          {/* Personal info */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-slate-900">Información personal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nombre completo">
                <input value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} className="input" />
              </Field>
              <Field label="Correo electrónico">
                <input value={authUser?.email || ""} readOnly className="input bg-slate-50 cursor-not-allowed text-slate-500" />
              </Field>
              <Field label="Teléfono">
                <PhoneInput value={account.phone} onChange={(digits) => setAccount({ ...account, phone: digits })} />
              </Field>
            </div>
            <div className="flex justify-end">
              <button onClick={saveAccount} disabled={savingAccount} className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
                {savingAccount ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>

          {/* Password */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Lock className="w-4 h-4" />Cambiar contraseña</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Contraseña actual">
                  <input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="input" />
                </Field>
              </div>
              <Field label="Nueva contraseña">
                <input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="input" />
              </Field>
              <Field label="Confirmar nueva contraseña">
                <input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="input" />
              </Field>
            </div>
            <div className="flex justify-end">
              <button onClick={changePassword} disabled={savingPw} className="px-4 py-2 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-60">
                {savingPw ? "Actualizando…" : "Cambiar contraseña"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "clinic" && (
        <div className="space-y-4">
          {/* Clinic info */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-slate-900">Datos de la clínica</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nombre de la clínica">
                <input value={clinic.name} onChange={(e) => setClinic({ ...clinic, name: e.target.value })} className="input" />
              </Field>
              <Field label="Teléfono">
                <PhoneInput value={clinic.phone} onChange={(digits) => setClinic({ ...clinic, phone: digits })} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Dirección">
                  <input value={clinic.address} onChange={(e) => setClinic({ ...clinic, address: e.target.value })} className="input" />
                </Field>
              </div>
              <Field label="RFC (opcional)">
                <input value={clinic.rfc} onChange={(e) => setClinic({ ...clinic, rfc: e.target.value })} placeholder="XXXX010101XXX" className="input" />
              </Field>
              <Field label="Sitio web (opcional)">
                <input value={clinic.website} onChange={(e) => setClinic({ ...clinic, website: e.target.value })} placeholder="https://miclinica.mx" className="input" />
              </Field>
            </div>
            <div className="flex justify-end">
              <button onClick={saveClinic} disabled={savingClinic} className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
                {savingClinic ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Upload className="w-4 h-4" />Logo de la clínica</h2>
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <Building2 className="w-8 h-8 text-slate-300" />
                )}
              </div>
              <div className="space-y-2">
                <label className="block">
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" id="logo-input" />
                  <label htmlFor="logo-input" className="cursor-pointer text-sm text-brand-electric hover:underline font-medium">
                    Seleccionar imagen
                  </label>
                </label>
                <p className="text-xs text-slate-500">PNG, JPG o SVG — máx 2MB</p>
                {logoFile && (
                  <button onClick={uploadLogo} disabled={uploadingLogo} className="text-sm px-3 py-1.5 rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
                    {uploadingLogo ? "Subiendo…" : "Subir logo"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; } .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
